import { Box, SelectChangeEvent, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { AddressInput } from '@/components/Form/AddressInput/AddressInput';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { ADDRESS_MAX_LENGTH } from '@/constants/places';
import { CAYMAN_REGIONS_OPTIONS } from '@/constants/tenant-configs/cayman';

import { useTranslate } from '@/hooks/useTranslate';

import { Option } from '@/types/components/common';
import {
  AddressFormLine1Details,
  AddressForm as AddressFormType,
} from '@/types/form';
import { CountryISO } from '@/types/places';

import { getDataTestId } from '@/utils/browser';

import { AddressFieldsProps } from './AddressFields';
import {
  getCaymanDistrictOptions,
  getCaymanRegionByDistrict,
  getValidRegion,
  getValidValue,
} from './helpers';

const getLine1PlaceKey = (address: AddressFormLine1Details) =>
  address.place_id ?? JSON.stringify(address);

const CaymanAddressFields = ({
  isPending,
  popularCountryOptions,
}: AddressFieldsProps) => {
  const { control, clearErrors, setValue } = useFormContext<AddressFormType>();
  const { t } = useTranslate();

  const line1 = useWatch({ control, name: 'line1' });
  const state = useWatch({ control, name: 'state' });
  const [placeObject, setPlaceObject] = useState<
    AddressFormLine1Details | undefined
  >();

  const [districtOptions, setDistrictOptions] = useState<Option[]>([]);
  const districtOptionsRef = useRef<Option[]>([]);

  const lastLine1PlaceIdRef = useRef<string | undefined>(undefined);
  const countryDiffersFromPlaceRef = useRef(false);

  const handleAfterRegionSelect = (event: SelectChangeEvent<unknown>) => {
    const newRegion = event.target.value;
    if (newRegion !== state) {
      setValue('city', '');
      setValue('poBoxDistrict', '');
      clearErrors(['city', 'poBoxDistrict']);
    }
  };

  const handleAfterCountrySelect = (event: SelectChangeEvent<unknown>) => {
    const next = String(event.target.value);
    const placeCountry = typeof line1 === 'object' ? line1.country : undefined;
    const placeId = typeof line1 === 'object' ? line1.place_id : undefined;
    if (placeId && placeCountry && next !== placeCountry) {
      countryDiffersFromPlaceRef.current = true;
    } else if (placeId && placeCountry && next === placeCountry) {
      countryDiffersFromPlaceRef.current = false;
    }
  };

  useEffect(() => {
    if (state) {
      const newDistrictOptions = getCaymanDistrictOptions(state);
      districtOptionsRef.current = newDistrictOptions;
      setDistrictOptions(newDistrictOptions);
    }
  }, [state]);

  useEffect(() => {
    if (typeof line1 === 'object') {
      const placeKey = getLine1PlaceKey(line1);
      if (placeKey !== lastLine1PlaceIdRef.current) {
        setPlaceObject(line1);
      }
      if (line1.country === CountryISO.Cayman) {
        let newDistrictOptions: Option[] = [...districtOptionsRef.current];

        if (line1.state) {
          newDistrictOptions = getCaymanDistrictOptions(line1.state);
        } else if (line1.city) {
          const foundRegion = getCaymanRegionByDistrict({
            city: line1.city,
            poBoxDistrict: line1.poBoxDistrict,
          });
          if (foundRegion) {
            newDistrictOptions = getCaymanDistrictOptions(foundRegion);
          }
        }
        districtOptionsRef.current = newDistrictOptions;
        setDistrictOptions(newDistrictOptions);
      }
    } else {
      lastLine1PlaceIdRef.current = undefined;
      countryDiffersFromPlaceRef.current = false;
    }
  }, [line1]);

  useEffect(() => {
    if (!placeObject) {
      return;
    }

    if (placeObject.country !== CountryISO.Cayman) {
      if (!countryDiffersFromPlaceRef.current) {
        setValue('country', placeObject.country ?? '');
      }
      setValue('city', '');
      setValue('poBoxDistrict', '');
      clearErrors(['city', 'poBoxDistrict', 'country', 'line1']);
      lastLine1PlaceIdRef.current = getLine1PlaceKey(placeObject);
      setPlaceObject(undefined);
      return;
    }

    const options = districtOptionsRef.current;
    if (options.length === 0) {
      return;
    }

    if (placeObject.state) {
      setValue('state', getValidRegion(placeObject.state));
    } else if (placeObject.city) {
      const foundRegion = getCaymanRegionByDistrict({
        city: placeObject.city,
        poBoxDistrict: placeObject.poBoxDistrict,
      });
      setValue('state', getValidRegion(foundRegion));
    }
    setValue(
      'city',
      getValidValue({
        value1: placeObject.city,
        value2: placeObject.poBoxDistrict,
        options,
      }),
    );
    setValue(
      'poBoxDistrict',
      getValidValue({
        value1: placeObject.poBoxDistrict,
        value2: placeObject.city,
        options,
      }),
    );
    setValue('zip', placeObject.zip ?? '');
    if (!countryDiffersFromPlaceRef.current) {
      setValue('country', placeObject.country ?? '');
    }
    clearErrors(['city', 'state', 'zip', 'country', 'line1', 'poBoxDistrict']);
    lastLine1PlaceIdRef.current = getLine1PlaceKey(placeObject);
    setPlaceObject(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeObject]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={theme => theme.spacing(3.5)}
      {...getDataTestId(`cayman-address-fields`)}
    >
      <Controller
        name="line1"
        control={control}
        rules={{
          required: t(
            'createNewAccount.addressPage.form.physicalAddress.error.required',
          ),
        }}
        render={({ field, fieldState }) => (
          <AddressInput
            {...field}
            control={control}
            textFieldProps={{
              label: t(
                'createNewAccount.addressPage.form.physicalAddress.label',
              ),
              error: !!fieldState.error,
              helperText:
                fieldState.error?.message ||
                (typeof field.value === 'string' &&
                  field.value.length >= ADDRESS_MAX_LENGTH.line1 &&
                  t(
                    'createNewAccount.addressPage.form.physicalAddress.error.maximum',
                    {
                      value: `${ADDRESS_MAX_LENGTH.line1}`,
                    },
                  )),
              inputProps: {
                maxLength: ADDRESS_MAX_LENGTH.line1,
              },
            }}
            disabled={isPending}
          />
        )}
      />
      <Controller
        name="city"
        control={control}
        rules={{
          required: t(
            'createNewAccount.addressPage.form.district.error.required',
          ),
        }}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            control={control}
            options={districtOptions}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            disabled={false}
            label={t('createNewAccount.addressPage.form.district.label')}
            placeholder={t(
              'createNewAccount.addressPage.form.district.placeHolder',
            )}
            useDrawerOnMobile
            withSearchBar
          />
        )}
      />
      <Controller
        name="poBox"
        control={control}
        rules={{
          required: t('createNewAccount.addressPage.form.poBox.error.required'),
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ||
              ((field.value?.length ?? 0) >= ADDRESS_MAX_LENGTH.poBox &&
                t('createNewAccount.addressPage.form.poBox.error.maximum', {
                  value: `${ADDRESS_MAX_LENGTH.poBox}`,
                }))
            }
            disabled={false}
            fullWidth
            label={t('createNewAccount.addressPage.form.poBox.label')}
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH.poBox,
            }}
          />
        )}
      />
      <Controller
        name="poBoxDistrict"
        control={control}
        rules={{
          required: t(
            'createNewAccount.addressPage.form.poBoxAddressDistrict.error.required',
          ),
        }}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            control={control}
            options={districtOptions}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            disabled={false}
            label={t(
              'createNewAccount.addressPage.form.poBoxAddressDistrict.label',
            )}
            placeholder={t(
              'createNewAccount.addressPage.form.poBoxAddressDistrict.placeHolder',
            )}
            useDrawerOnMobile
            withSearchBar
          />
        )}
      />
      <Controller
        name="zip"
        control={control}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ||
              ((field.value?.length ?? 0) >= ADDRESS_MAX_LENGTH.zip &&
                t(
                  'createNewAccount.addressPage.form.postalCode.error.maximum',
                  {
                    value: ADDRESS_MAX_LENGTH.zip,
                  },
                ))
            }
            disabled={false}
            fullWidth
            label={t('createNewAccount.addressPage.form.postalCode.label')}
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH.zip,
            }}
          />
        )}
      />

      <Controller
        name="state"
        control={control}
        rules={{
          required: t(
            'createNewAccount.addressPage.form.region.error.required',
          ),
        }}
        render={({ field, fieldState }) => {
          return (
            <Select
              {...field}
              control={control}
              options={CAYMAN_REGIONS_OPTIONS}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              disabled={false}
              label={t('createNewAccount.addressPage.form.region.label')}
              placeholder={t(
                'createNewAccount.addressPage.form.region.placeHolder',
              )}
              useDrawerOnMobile
              withSearchBar
              onSelectChange={handleAfterRegionSelect}
            />
          );
        }}
      />

      <Controller
        name="country"
        control={control}
        rules={{
          required: 'Country is required',
        }}
        render={({ field, fieldState }) => {
          return (
            <Select
              {...field}
              control={control}
              options={popularCountryOptions}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              disabled={isPending}
              label={t('createNewAccount.addressPage.form.country.label')}
              placeholder={t(
                'createNewAccount.addressPage.form.country.placeHolder',
              )}
              useDrawerOnMobile
              withSearchBar
              onSelectChange={handleAfterCountrySelect}
            />
          );
        }}
      />

      <Box display="flex" flexDirection="row" mt={-3} alignItems="center">
        <Icon name="alert" color="primary400" />
        <Typography variant="captionBold">
          {t('createNewAccount.addressPage.caymanResidentInstruction')}
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(CaymanAddressFields);
